import React, { useState, useEffect, useRef, useCallback } from 'react';
import RestaurantSidebar from './RestaurantSidebar';
import RestaurantHeader from './RestaurantHeader';
import socketService from '../services/socket';
import { useAuth } from '../context/AuthContext';
import { respondToPreviewRequest } from '../utils/printPreviewResponder';

// Toast notification types
const TOAST_TYPES = {
  SUCCESS: 'success',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error'
};

// Flutter App Theme Colors (exact match)
const theme = {
  backgroundColor: '#F8FAFC',
  primaryColor: '#FF6961',
};

function RestaurantLayout({ children, orderCount = 0 }) {
  const { restaurant } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Toast state
  const toastIdRef = useRef(0);
  const [toasts, setToasts] = useState([]);

  // Call modal state
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);

  const addToast = useCallback((message, type = TOAST_TYPES.INFO, duration = 5000) => {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Global socket listeners for call functionality
  useEffect(() => {
    if (!restaurant?._id) return;

    socketService.connect();
    socketService.joinRestaurantRoom(restaurant._id);

    // Listen for incoming calls
    const unsubIncomingCall = socketService.on('incoming_call', (data) => {
      console.log('📞 Incoming call:', data);
      setIncomingCall({
        callId: data.callId,
        callerName: data.callerName || data.customerName || 'Customer',
        callerPhone: data.callerPhone || data.phone,
        orderNumber: data.orderNumber,
        callType: data.callType || 'voice'
      });
      addToast(`📞 Incoming call from ${data.callerName || 'Customer'}`, TOAST_TYPES.INFO, 10000);
    });

    // Listen for call accepted (by restaurant or customer)
    const unsubCallAccepted = socketService.on('call_accepted', (data) => {
      console.log('✅ Call accepted:', data);
      setIncomingCall(null);
      setActiveCall({
        callId: data.callId,
        callerName: data.callerName || data.customerName || 'Customer',
        startTime: new Date()
      });
      addToast('✅ Call connected', TOAST_TYPES.SUCCESS);
    });

    // Listen for call rejected
    const unsubCallRejected = socketService.on('call_rejected', (data) => {
      console.log('❌ Call rejected:', data);
      setIncomingCall(null);
      setActiveCall(null);
      addToast(`📵 Call was declined${data.reason ? ': ' + data.reason : ''}`, TOAST_TYPES.WARNING);
    });

    // Listen for call ended
    const unsubCallEnded = socketService.on('call_ended', (data) => {
      console.log('📴 Call ended:', data);
      setIncomingCall(null);
      setActiveCall(null);
      const duration = data.duration ? ` (${Math.floor(data.duration / 60)}:${String(data.duration % 60).padStart(2, '0')})` : '';
      addToast(`📴 Call ended${duration}`, TOAST_TYPES.INFO);
    });

    // Listen for missed calls
    const unsubCallMissed = socketService.on('call_missed', (data) => {
      console.log('📵 Missed call:', data);
      setIncomingCall(null);
      const callerName = data.callerName || data.customerName || 'Customer';
      addToast(`📵 Missed call from ${callerName}`, TOAST_TYPES.WARNING, 8000);
    });

    // Admin asked this terminal to render an exact print preview → render + upload.
    // No-op on browsers (renderExactPreview reports "terminal only"); only the POS
    // desktop app can produce the true raster.
    const unsubPreviewReq = socketService.on('request_print_preview', (data) => {
      console.log('🖨️ Admin requested a live print preview:', data);
      respondToPreviewRequest(data);
    });

    return () => {
      unsubIncomingCall();
      unsubCallAccepted();
      unsubCallRejected();
      unsubCallEnded();
      unsubCallMissed();
      unsubPreviewReq();
    };
  }, [restaurant?._id, addToast]);

  // Handle accepting a call
  const handleAcceptCall = () => {
    if (incomingCall) {
      socketService.emit('accept_call', { callId: incomingCall.callId });
      // The call_accepted event will handle UI updates
    }
  };

  // Handle rejecting a call
  const handleRejectCall = () => {
    if (incomingCall) {
      socketService.emit('reject_call', { callId: incomingCall.callId });
      setIncomingCall(null);
    }
  };

  // Handle ending a call
  const handleEndCall = () => {
    if (activeCall) {
      socketService.emit('end_call', { callId: activeCall.callId });
      setActiveCall(null);
    }
  };

  // Responsive sidebar width matching RestaurantSidebar clamp values
  const sidebarWidth = isMobile ? 0 : (isCollapsed ? 'clamp(60px, 8vw, 72px)' : 'clamp(220px, 20vw, 260px)');

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.backgroundColor,
      display: 'flex',
    }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <div style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 1000,
        }}>
          <RestaurantSidebar
            isCollapsed={isCollapsed}
            onToggle={() => setIsCollapsed(!isCollapsed)}
            isMobile={false}
            orderCount={orderCount}
          />
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {isMobile && isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setIsMobileMenuOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
            }}
          />
          {/* Sidebar */}
          <div style={{
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 1000,
          }}>
            <RestaurantSidebar
              isCollapsed={false}
              isMobile={true}
              onClose={() => setIsMobileMenuOpen(false)}
              orderCount={orderCount}
            />
          </div>
        </>
      )}

      {/* Mobile Header */}
      {isMobile && (
        <header style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '56px',
          background: `linear-gradient(135deg, ${theme.primaryColor} 0%, #E6524A 100%)`,
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
          zIndex: 998,
          boxShadow: '0 2px 8px rgba(255, 105, 97, 0.2)',
        }}>
          {/* Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '8px',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {/* Logo / Title */}
          <span style={{
            flex: 1,
            textAlign: 'center',
            color: 'white',
            fontSize: '18px',
            fontWeight: '600',
          }}>
            Foodwinc Restaurant
          </span>

          {/* Placeholder for symmetry */}
          <div style={{ width: '40px' }} />
        </header>
      )}

      {/* Page Content */}
      <main style={{
        flex: 1,
        marginLeft: sidebarWidth,
        marginTop: isMobile ? '56px' : 0,
        minHeight: isMobile ? 'calc(100vh - 56px)' : '100vh',
        transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100vw',
        overflowX: 'hidden',
      }}>
        {/* Desktop Header */}
        {!isMobile && <RestaurantHeader />}
        <div style={{
          flex: 1,
          overflowX: 'auto',
          padding: isMobile ? '16px' : '24px',
        }}>
          {children}
        </div>
      </main>

      {/* Toast Notifications */}
      {toasts.length > 0 && (
        <div style={{
          position: 'fixed',
          top: isMobile ? '70px' : '20px',
          right: '20px',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          maxWidth: '400px'
        }}>
          {toasts.map(toast => (
            <div
              key={toast.id}
              style={{
                padding: '16px 20px',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                animation: 'slideIn 0.3s ease',
                background: toast.type === TOAST_TYPES.SUCCESS ? '#10b981' :
                           toast.type === TOAST_TYPES.ERROR ? '#ef4444' :
                           toast.type === TOAST_TYPES.WARNING ? '#f59e0b' : '#3b82f6',
                color: 'white'
              }}
            >
              <span style={{ fontSize: '20px' }}>
                {toast.type === TOAST_TYPES.SUCCESS ? '✅' :
                 toast.type === TOAST_TYPES.ERROR ? '❌' :
                 toast.type === TOAST_TYPES.WARNING ? '⚠️' : 'ℹ️'}
              </span>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>
                  {toast.message}
                </p>
              </div>
              <button
                onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Incoming Call Modal */}
      {incomingCall && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          animation: 'fadeIn 0.2s ease'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '24px',
            padding: '32px',
            width: '90%',
            maxWidth: '360px',
            textAlign: 'center',
            animation: 'scaleIn 0.3s ease'
          }}>
            {/* Caller Avatar */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FF6961 0%, #E6524A 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              animation: 'pulse 1.5s infinite'
            }}>
              <span style={{ fontSize: '40px' }}>📞</span>
            </div>

            <h3 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: '700', color: '#1F2937' }}>
              Incoming Call
            </h3>
            <p style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: '600', color: '#374151' }}>
              {incomingCall.callerName}
            </p>
            {incomingCall.orderNumber && (
              <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#6B7280' }}>
                Order #{incomingCall.orderNumber}
              </p>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
              {/* Reject Button */}
              <button
                onClick={handleRejectCall}
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  border: 'none',
                  background: '#ef4444',
                  color: 'white',
                  fontSize: '28px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                  transition: 'transform 0.2s ease'
                }}
              >
                📵
              </button>
              {/* Accept Button */}
              <button
                onClick={handleAcceptCall}
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  border: 'none',
                  background: '#10b981',
                  color: 'white',
                  fontSize: '28px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                  transition: 'transform 0.2s ease'
                }}
              >
                📞
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Call Indicator */}
      {activeCall && (
        <div style={{
          position: 'fixed',
          top: isMobile ? '70px' : '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#10b981',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '50px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
          zIndex: 10001
        }}>
          <span style={{ animation: 'pulse 1.5s infinite' }}>📞</span>
          <span style={{ fontWeight: '600' }}>
            On call with {activeCall.callerName}
          </span>
          <button
            onClick={handleEndCall}
            style={{
              background: '#ef4444',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px'
            }}
          >
            ✕
          </button>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
}

export default RestaurantLayout;
